import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

/**
 * A full-screen "3 … 2 … 1 … GO!" overlay shown for the first three seconds
 * of a LIVE session so athletes get a visible beat to start on. Self-contained:
 * parent just mounts it on the PRESTART → LIVE edge and unmounts it after.
 *
 * Each digit pops in with a big scale-down+fade and a matching sonar ring.
 * The final "GO!" card explodes outward with an orange shockwave. We never
 * play audio — coaches run their own PA; the TV stays silent by design.
 */
interface Props {
  /** Fired once the GO! frame has finished — parent should unmount. */
  onDone: () => void
}

const STEPS = ['3', '2', '1', 'GO!'] as const
const STEP_MS = 800

export default function CountInCard({ onDone }: Props) {
  const [i, setI] = useState(0)

  useEffect(() => {
    if (i >= STEPS.length) {
      const t = setTimeout(onDone, 350)
      return () => clearTimeout(t)
    }
    const t = setTimeout(() => setI(i + 1), STEP_MS)
    return () => clearTimeout(t)
  }, [i, onDone])

  const current = STEPS[i]
  const isGo = current === 'GO!'

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none">
      {/* Backdrop darkens ring-by-ring — first step at 0.6, holds until GO */}
      <motion.div
        className="absolute inset-0"
        initial={{ backgroundColor: 'rgba(0,0,0,0)' }}
        animate={{
          backgroundColor: isGo
            ? 'rgba(201,58,48,0.25)'
            : 'rgba(0,0,0,0.6)',
        }}
        transition={{ duration: 0.3 }}
      />

      <AnimatePresence mode="wait">
        {current && (
          <motion.div
            key={current}
            initial={{ scale: 2.2, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.6, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 280, damping: 18 }}
            className="relative"
          >
            {/* Sonar ring */}
            <motion.div
              key={`${current}-ring`}
              initial={{ scale: 0.4, opacity: 0.6 }}
              animate={{ scale: 2.4, opacity: 0 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              className="absolute inset-0 rounded-full"
              style={{
                border: `2px solid ${isGo ? '#C93A30' : '#ffffff'}`,
              }}
            />

            <div
              className={`
                relative rounded-full flex items-center justify-center
                ${isGo ? 'w-[28rem] h-[28rem]' : 'w-[20rem] h-[20rem]'}
              `}
              style={{
                background: isGo
                  ? 'radial-gradient(circle, rgba(201,58,48,0.35), rgba(201,58,48,0.05))'
                  : 'radial-gradient(circle, rgba(255,255,255,0.1), rgba(241, 238, 232, 0.02))',
                border: `2px solid ${isGo ? 'rgba(201,58,48,0.6)' : 'rgba(241, 238, 232, 0.15)'}`,
                boxShadow: isGo
                  ? '0 0 120px rgba(201,58,48,0.5)'
                  : '0 0 60px rgba(241, 238, 232, 0.08)',
              }}
            >
              <span
                className="font-black tabular-nums leading-none"
                style={{
                  fontSize: isGo ? '10rem' : '14rem',
                  color: isGo ? '#C93A30' : '#ffffff',
                  textShadow: isGo
                    ? '0 0 40px rgba(201,58,48,0.7)'
                    : '0 0 30px rgba(241, 238, 232, 0.2)',
                  letterSpacing: isGo ? '0.02em' : '-0.05em',
                }}
              >
                {current}
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
